import requestAPI from '@/logic/requestBeApi';
import applyData from './applyData';
import store from '@/store';
import TrainingHelper from '@/views/CandidateApp/TrainingHelper';

const asyncOperations = {
  async getBusyDays(self: Record<string, any>): Promise<void> {
    if (self.userRole !== 'assessor' || !self.calendarExists) return;

    const { dateFrom, dateTo } = self.getDateRangeForCurrentMonth();

    self.processingGetTakenSlotsRequest = true;

    const resp = await requestAPI({
      url: `/api/v1/assessors/me/assessment-dates?assessment_date[from]=${dateFrom}&assessment_date[to]=${dateTo}`,
      method: 'GET',
    });

    if (resp.ok) {
      const assessmentsArray = await resp.json();

      applyData.calRef = self;
      applyData.applyAssessmentsToCalendar(self, assessmentsArray);

      this.getDeadlines(self);
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    self.processingGetTakenSlotsRequest = false;
  },

  async getDeadlinesForTrainings(
    self: Record<string, any>,
    activeTrainingId?: string,
    trainingCandidateId?: string,
  ): Promise<void> {
    self.processingGetTakenSlotsRequest = true;

    let url = '';
    if (activeTrainingId) {
      url = `/api/v1/trainings/${activeTrainingId}`;
    } else {
      url = '/api/v1/training-sessions';
    }

    const resp = await requestAPI({
      url: url,
      method: 'GET',
    });

    if (resp.ok) {
      const deadlinesArray = await resp.json();

      applyData.calRef = self;

      let sessionsAndTasks = [];
      if (activeTrainingId) {
        sessionsAndTasks = TrainingHelper.processTrainingSessionsFromObject(deadlinesArray).concat(
          TrainingHelper.processTasksFromObject(deadlinesArray, trainingCandidateId),
        );
      } else {
        sessionsAndTasks = TrainingHelper.processTrainingSessionsFromArray(deadlinesArray)
          .concat()
          .concat(TrainingHelper.processTasksFromArray(deadlinesArray));
      }

      applyData.applyDeadlinesToCalendar(self, sessionsAndTasks);
    } else if (resp.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
    }

    self.processingGetTakenSlotsRequest = false;
  },

  async getClientDeadlinesForTrainings(
    self: Record<string, any>,
    activeTrainingId?: string,
  ): Promise<void> {
    self.processingGetTakenSlotsRequest = true;

    let url = '';
    if (activeTrainingId) {
      url = `/api/v1/trainings/${activeTrainingId}`;
    } else {
      url = '/api/v1/training-sessions';
    }

    const resp = await requestAPI({
      url: url,
      method: 'GET',
    });

    if (resp.ok) {
      const deadlinesArray = await resp.json();

      applyData.calRef = self;

      let sessions = [];
      if (activeTrainingId) {
        sessions = TrainingHelper.processClientTrainingSessionsFromObject(
          deadlinesArray,
          activeTrainingId,
        );
      } else {
        sessions = TrainingHelper.processClientTrainingSessionsFromArray(deadlinesArray);
      }

      applyData.applyDeadlinesToCalendar(self, sessions);
    } else if (resp.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
    }
    self.processingGetTakenSlotsRequest = false;
  },

  async getDeadlines(self: Record<string, any>): Promise<void> {
    self.processingGetTakenSlotsRequest = true;

    let query = '';
    let url = '';
    if (self.userRole === 'assessor') {
      query =
        'assessment_request_assessors.status[]=confirmed&assessment_request_assessors.status[]=co_assessor_accepted';
      url = `/api/v1/${self.userRole}s/me/assessment-requests?${query}`;
    } else if (self.userRole === 'client') {
      query = 'status=confirmed';
      url = `/api/v1/${self.userRole}s/me/assessment-requests?${query}`;

      const resp = await requestAPI({
        url: url,
        method: 'GET',
      });

      if (resp.ok) {
        const deadlinesArray = await resp.json();
        applyData.calRef = self;
        applyData.applyDeadlinesToCalendar(self, deadlinesArray);
      } else {
        self.$message.error(self.$t('someErrorOccurred'));
      }
      self.processingGetTakenSlotsRequest = false;
    }
  },

  async getPreferredWorkingDays(self: Record<string, any>): Promise<void> {
    if (self.userRole === 'client' || self.userRole === 'candidate') return;
    self.fetchPreferredWorkingDays = true;

    const resp = await requestAPI({ url: '/api/v1/assessors/me', method: 'GET' });

    if (resp.ok) {
      const data = await resp.json();
      applyData.applyPreferredWorkingDaysToCalendar(data.working_days);
      self.hoursBottomRange = data.working_hours?.split('-')[0];
      self.hoursTopRange = data.working_hours?.split('-')[1];
      store.commit('setAssessorWorkingHours', data.working_hours);
      localStorage.setItem('assessorWorkingHours', data.working_hours);
      store.commit('setUserPhone', { number: data.company?.phones[0], type: 'company' });
      store.commit('setUserPhone', { number: data.contact_details?.phones[0], type: 'personal' });
      store.commit('setUserWebCommunicator', {
        details: data.contact_details?.skype,
        type: 'skype',
      });
      store.commit('setUserWebCommunicator', { details: data.contact_details?.zoom, type: 'zoom' });
      store.commit('setUserWebCommunicator', {
        details: data.contact_details?.whereby,
        type: 'whereby',
      });
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    self.fetchPreferredWorkingDays = false;
  },

  async getProject(self: Record<string, any>): Promise<void> {
    const projectDeadlineNotRequired =
      (self.pureCalendar && !self.isInModal) || self.pureCalendarForMobile;
    if (projectDeadlineNotRequired || self.userRole === 'candidate') return;

    self.fetchDeadlineForProject = true;

    const resp = await requestAPI({
      url: `/api/v1/assessments/${self.assessmentId}`,
      method: 'GET',
    });

    if (resp.ok) {
      const data = await resp.json();
      this.getProjectDeadline(data.assessment_request_id, self);
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    self.fetchDeadlineForProject = false;
  },

  async getProjectDeadline(projectId: string, self: Record<string, any>): Promise<void> {
    self.fetchDeadlineForProject = true;

    const resp = await requestAPI({
      url: `/api/v1/assessment-requests/${projectId}`,
      method: 'GET',
    });

    if (resp.ok) {
      const data = await resp.json();
      self.projectDeadline = data.deadline;
      self.adjustCalendarStyles('Project deadline fetched');
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    self.fetchDeadlineForProject = false;
  },
};

export default asyncOperations;
